import { createContext, useContext } from 'react'
import { ApiClient, initApiClient } from '@frontend/lib/request'

import { UITransportationListFilterParams } from '@shared-types/domain'
import {
  Transportation,
  TransportationCreationPayload,
  TransportationShipmentCreationPayload,
  TransportationShipmentsUpdatePayload,
  TransportationStatusUpdatePayload,
  TransportationUpdatePayload,
} from '@shared-types/transportations'
import { Collection } from '@shared-types/util'

interface TransportationClient {
  list: (
    transportationParams: UITransportationListFilterParams
  ) => Promise<Collection<Transportation>>
  create: (payload: TransportationCreationPayload) => Promise<Transportation>
  update: (id: string, payload: TransportationUpdatePayload) => Promise<Transportation>
  updateShipments: (
    id: string,
    payload: TransportationShipmentsUpdatePayload
  ) => Promise<Transportation>
  updateStatus: (id: string, payload: TransportationStatusUpdatePayload) => Promise<Transportation>
  createShipment: (
    id: string,
    payload: TransportationShipmentCreationPayload
  ) => Promise<Transportation>
}

class DefaultTransportationsClient implements TransportationClient {
  private client: ApiClient

  constructor() {
    this.client = initApiClient({})
  }

  async list(params: UITransportationListFilterParams) {
    return this.client.get(`/transportations`, { params }).then((res) => res.data)
  }

  async create(payload: TransportationCreationPayload) {
    return this.client.post(`/transportations`, payload).then((res) => res.data)
  }

  async update(id: string, payload: TransportationUpdatePayload) {
    return this.client.patch(`/transportations/${id}`, payload).then((res) => res.data)
  }

  async updateShipments(id: string, payload: TransportationShipmentsUpdatePayload) {
    return this.client
      .put(`/transportations/${id}/transporting_shipments`, payload)
      .then((res) => res.data)
  }

  async updateStatus(id: string, payload: TransportationStatusUpdatePayload) {
    return this.client
      .put(`/transportations/${id}/transportation_status`, payload)
      .then((res) => res.data)
  }

  async createShipment(id: string, payload: TransportationShipmentCreationPayload) {
    return this.client.post(`/transportations/${id}/shipments`, payload).then((res) => res.data)
  }
}

const TransportationClientContext = createContext<TransportationClient>(
  new DefaultTransportationsClient()
)

export function useTransportationClient() {
  return useContext(TransportationClientContext)
}
